import { useEffect, useState, useContext } from 'react'
import dynamic from 'next/dynamic'
import ButtonSp from '../ui/ButtonSp'
import DropdownMenuSp from '../ui/DropdownMenuSp'
const TrackingPopover = dynamic(import('./TrackingPopover'))
import {
  Container,
  AppBar,
  Toolbar,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Grid,
  Collapse,
  Divider,
  makeStyles,
  useTheme,
  Hidden,
} from '@material-ui/core'
import ExpandLessIcon from '@material-ui/icons/ExpandLess'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import MenuIcon from '@material-ui/icons/Menu'
import CloseIcon from '@material-ui/icons/Close'
import clsx from 'clsx'
import dayjs from 'dayjs'
import getConfig from 'next/config'
import PdpaUtility from '../utility/pdpaUtility'
import { useTranslation } from 'next-i18next'
import Link from '../utility/CustomLink'
import { useRouter } from 'next/router'
import Cookies from 'universal-cookie'
import { Context } from '../../context'

import useFestivalTheme from '../../utils/hooks/useFestivalTheme'
import { isOldWorld } from '../../utils/isOldWorld'

const { publicRuntimeConfig } = getConfig()

const useStyles = makeStyles((theme) => ({
  headerOuter: {
    backgroundColor: 'white',
    height: theme.homeStyle.headerHeightDesktop,
    width: '100vw',
    padding: `${theme.spacing(1)}px 0`,
    boxShadow: '3px 0px 5px grey',
    position: 'fixed',
    top: 0,
    zIndex: 1000,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  headerMain: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    fontWeight: 400,
  },
  navDiv: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    '& ul': {
      display: 'flex',
      flexDirection: 'row',
      listStyleType: 'none',
      alignItems: 'center',
      margin: theme.spacing(1),
      '& li': {
        margin: `0 ${theme.spacing(1)}px`,
        textDecoration: 'none',
        color: '#333333',
        cursor: 'pointer',
        fontSize: 18,
        [theme.breakpoints.down('lg')]: {
          fontSize: 14,
        },
      },
      '& li:hover': {
        color: theme.palette.primary.main,
      },
    },
  },
  headerRight: {
    fontSize: 18,
    '& p': {
      marginRight: theme.spacing(2),
      color: '#333333',
    },
    [theme.breakpoints.down('lg')]: {
      fontSize: 14,
    },
  },
  regisBtn: {
    height: 42,
  },
  cursorPointer: {
    cursor: 'pointer',
  },
  pl1: {
    paddingLeft: 2,
  },
  pl4: {
    paddingLeft: theme.spacing(3),
  },
  pl7: {
    paddingLeft: theme.spacing(6),
  },
  headerSpacerMobile: {
    width: '100%',
    height: theme.homeStyle.headerHeightMobile,
  },
  headerSpacerDesktop: {
    width: '100%',
    height: theme.homeStyle.headerHeightDesktop,
  },
  activeLink: {
    color: `${theme.palette.primary.main} !important`,
  },
  langSwitch: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(1),
  },
  langSwitchTextInactive: {
    cursor: 'pointer',
    color: '#BDBDBD',
  },
  langSwitchSeparator: {
    height: 16,
    width: 1,
    backgroundColor: '#E0E0E0',
    margin: `0 ${theme.spacing(1)}px`,
  },
  appBar: {
    borderBottom: '1px solid #F2F2F2',
  },
  listItemPaddingY: {
    paddingTop: theme.spacing(1.5),
    paddingBottom: theme.spacing(1.5),
  },
  login: {
    color: '#404041',
    cursor: 'pointer',
    '&:hover': {
      color: theme.palette.primary.main,
    },
  },
  loggedInNameDiv: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    cursor: 'pointer',
    '& dl': {
      margin: 0,
    },
  },
  memberArea: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    height: 44,
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
  },
  chevronIcon: {
    height: 11,
    width: 11,
    marginLeft: theme.spacing(0.5),
  },
}))

const ChevronIcon = ({ className }) => (
  <svg
    width="12"
    height="8"
    viewBox="0 0 12 8"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
  >
    <path
      d="M1.41016 1.91406L5.91016 6.41406L10.4102 1.91406"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const Header = ({ userData }) => {
  const { onRouting } = useContext(Context)

  const { asset, logoStyle } = useFestivalTheme()

  const [isDrawerOpen, setDrawerOpen] = useState(false)
  const [isHelpOpen, setHelpOpen] = useState(false)
  const [isMemberMobileOpen, setMemberMobileOpen] = useState(false)
  const [isOurServiceOpen, setOurServiceOpen] = useState(false)
  const [currentPath, setCurrentPath] = useState('')
  const [isToolsOpen, setToolsOpen] = useState(false)
  const [isFaqOpen, setFaqOpen] = useState(false)

  const theme = useTheme()
  const classes = useStyles()

  const { t } = useTranslation('header')
  const Router = useRouter()

  // This is the OLD shared Header — keep internal nav in the old world by
  // appending `old` to link targets whenever the current route is old-world.
  const toOld = (href) =>
    isOldWorld(Router) ? `${href}${href.includes('?') ? '&' : '?'}old` : href

  const cookies = new Cookies()

  useEffect(() => {
    if (!Router.isReady) return
    setCurrentPath(Router.asPath)
  }, [Router.isReady])

  const changeLanguage = (nextLang, setCookie = true) => {
    const url = Router.asPath.split('?')
    const pathname = url[0]
    const query = url[1]

    onRouting(pathname, query, { isReplace: true, locale: nextLang })
    dayjs.locale(nextLang)
    if (setCookie) cookies.set('SHIPPOP_LOCALE', nextLang, { path: '/' })
  }

  // auto detect locale from query then cookie
  useEffect(() => {
    if (!Router.isReady) return
    if (
      Router.query.lang &&
      ['th', 'en'].includes(Router.query.lang.toLowerCase())
    ) {
      changeLanguage(Router.query.lang)
      return
    }
    if (
      cookies.get('SHIPPOP_LOCALE') &&
      cookies.get('SHIPPOP_LOCALE') !== Router.locale
    ) {
      changeLanguage(cookies.get('SHIPPOP_LOCALE'), false)
    }
  }, [Router.isReady])

  const isCurrentLink = (href) => {
    if (href === '/') return currentPath === '/'
    if (href === '/faq')
      return currentPath.includes('/faq') && !currentPath.includes('/inter/faq')
    return currentPath.includes(href)
  }

  const toggleHelp = () => {
    setHelpOpen(!isHelpOpen)
  }
  const toggleMemberMobile = () => {
    setMemberMobileOpen(!isMemberMobileOpen)
  }
  const toggleOurService = () => {
    setOurServiceOpen(!isOurServiceOpen)
  }
  const toggleTools = () => {
    setToolsOpen(!isToolsOpen)
  }
  const toggleFaq = () => {
    setFaqOpen(!isFaqOpen)
  }

  const helpMenus = [
    {
      label: t('contact-us'),
      href: toOld('/contact'),
    },
    {
      label: t('faq'),
      nested: [
        {
          label: t('domestic'),
          href: toOld('/faq'),
        },
        {
          label: t('inter'),
          href: toOld('/inter/faq'),
        },
      ],
    },
    {
      label: t('droppoints'),
      href: toOld('/nearby-dropoff'),
    },
    {
      label: t('other-tools'),
      nested: [
        {
          label: t('widgets'),
          href: toOld('/widget'),
        },
      ],
    },
  ]

  const ourServicesMenus = [
    {
      label: t('shippop-services'),
      href: toOld('/service'),
    },
    {
      label: t('for-developers'),
      href: toOld('/for-developers'),
    },
  ]

  const renderLangSwitch = () => {
    const isCurrentLang = (lang) => {
      if (lang === t('lang')) {
        return true
      }
      return false
    }
    return (
      <div className={classes.langSwitch}>
        <span
          className={clsx(
            !isCurrentLang('ไทย') && classes.langSwitchTextInactive
          )}
          onClick={() => changeLanguage('th')}
        >
          ไทย
        </span>
        <div className={classes.langSwitchSeparator} />
        <span
          className={clsx(
            !isCurrentLang('EN') && classes.langSwitchTextInactive
          )}
          onClick={() => changeLanguage('en')}
        >
          EN
        </span>
      </div>
    )
  }

  const handleLogout = (e = null) => {
    if (e) e.preventDefault()
    if (!localStorage.getItem('jwt_token')) return
    const randToken = JSON.parse(localStorage.getItem('shp_user')).user
      .randToken
    const currentUrl = `${publicRuntimeConfig.siteDomain}${Router.pathname}`
    const destUrl = currentUrl
    const checkAuthUrl = `${publicRuntimeConfig.siteDomain}/check-auth?destUrl=${destUrl}`
    const logoutUrl = `${
      publicRuntimeConfig.memberService
    }/sso/logout?logoutToken=${randToken}&cbUrl=${encodeURIComponent(
      checkAuthUrl
    )}`
    localStorage.removeItem('shp_user')
    localStorage.removeItem('jwt_token')
    PdpaUtility.clearAllLoginCookies()
    window.location.replace(logoutUrl)
  }

  const memberMenus = [
    {
      label: t('manage-shipment'),
      href: `${publicRuntimeConfig.appShippop}/`,
    },
    {
      label: t('member-dashboard'),
      href: `${publicRuntimeConfig.appShippop}/edit-profile/`,
    },
    {
      label: t('log-out'),
      handleClick: handleLogout,
      danger: true,
    },
  ]

  const renderMemberInfoDesktop = () => (
    <>
      <div className={classes.memberArea}>
        <img
          src={
            userData.facebook_id
              ? `https://graph.facebook.com/${userData.facebook_id}/picture?height=200&width=200`
              : '/static/img/icons/user-profile.svg'
          }
          alt="user"
          style={{
            width: 32,
            height: 32,
            marginRight: theme.spacing(2),
            borderRadius: '50%',
          }}
        />
        <DropdownMenuSp menuId="member-menu" menuObjects={memberMenus}>
          <div className={classes.loggedInNameDiv}>
            <dl>
              {userData.name.slice(0, 20)}
              {userData.name.length > 20 && '...'}
            </dl>
            <ExpandMoreIcon fontSize="smaill" />
          </div>
        </DropdownMenuSp>
      </div>
    </>
  )

  const renderListItemLink = (
    label,
    href,
    leftPaddingClass = classes.pl1,
    danger = false,
    onClick = () => {}
  ) => (
    <Link href={href || ''} passHref key={label}>
      <a title={label} onClick={onClick}>
        <ListItem
          button
          classes={{ root: classes.listItemPaddingY }}
          style={{ color: danger ? 'red' : 'auto' }}
        >
          <ListItemText
            primary={label}
            className={clsx(
              leftPaddingClass,
              isCurrentLink(href) && classes.activeLink
            )}
          />
        </ListItem>
      </a>
    </Link>
  )

  const renderListItemCollapse = (
    label,
    isOpen,
    toggle,
    child,
    leftPaddingClass = classes.pl1
  ) => (
    <>
      <ListItem
        button
        onClick={toggle}
        classes={{ root: classes.listItemPaddingY }}
      >
        <ListItemText primary={label} className={leftPaddingClass} />
        {isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
      </ListItem>
      <Collapse in={isOpen} timeout="auto" unmountOnExit>
        {child}
      </Collapse>
    </>
  )

  const renderMemberInfoMobile = () => (
    <>
      <ListItem
        button
        onClick={toggleMemberMobile}
        classes={{ root: classes.listItemPaddingY }}
      >
        <div className={classes.memberArea} style={{ width: '100%' }}>
          <img
            src={
              userData.facebook_id
                ? `https://graph.facebook.com/${userData.facebook_id}/picture?height=42&width=42`
                : '/static/img/icons/user-profile.svg'
            }
            alt="user"
            style={{
              width: 42,
              height: 42,
              marginRight: theme.spacing(2),
              borderRadius: '50%',
            }}
          />
          <dl
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              wordWrap: 'break-word',
              flexGrow: 1,
            }}
          >
            {userData.name}
          </dl>
          {isMemberMobileOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </div>
      </ListItem>
      <div>
        <Collapse in={isMemberMobileOpen} timeout="auto" unmountOnExit>
          <List component="div">
            {memberMenus.map((item) =>
              renderListItemLink(
                item.label,
                item.href,
                classes.pl4,
                item.danger,
                item.handleClick
              )
            )}
          </List>
        </Collapse>
      </div>
    </>
  )

  return (
    <>
      <Hidden implementation="css" smDown>
        <>
          <div className={classes.headerOuter}>
            <Container>
              <header className={classes.headerMain}>
                <div className={classes.navDiv}>
                  <Link href={toOld('/')} passHref>
                    <a
                      title="SHIPPOP ส่งของ ส่งพัสดุด่วน เปรียบเทียบราคาขนส่ง"
                      style={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                      }}
                    >
                      <img 
                        src={asset.logo}
                        className={logoStyle.header.className}
                        alt="SHIPPOP ส่งของ ส่งพัสดุด่วน เปรียบเทียบราคาขนส่ง"
                      />
                    </a>
                  </Link>
                  <nav>
                    <ul style={{ paddingLeft: 20 }}>
                      <DropdownMenuSp
                        menuId="services-menu"
                        menuObjects={ourServicesMenus}
                      >
                        <li
                          style={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                          }}
                        >
                          {t('our-services')}
                          <ChevronIcon className={classes.chevronIcon} />
                        </li>
                      </DropdownMenuSp>
                      <Link href={toOld('/pro')} passHref>
                        <a title="SHIPPOP PRO">
                          <li
                            className={clsx(
                              isCurrentLink('/pro') && classes.activeLink
                            )}
                          >
                            SHIPPOP PRO
                          </li>
                        </a>
                      </Link>
                      <Link href={toOld('/about')} passHref>
                        <a title={t('about-us')}>
                          <li
                            className={clsx(
                              isCurrentLink('/about') && classes.activeLink
                            )}
                          >
                            {t('about-us')}
                          </li>
                        </a>
                      </Link>
                      <TrackingPopover>
                        <li
                          className={clsx(
                            isCurrentLink('/tracking') && classes.activeLink
                          )}
                        >
                          {t('tracking')}
                        </li>
                      </TrackingPopover>
                      <DropdownMenuSp
                        menuId="help-menu"
                        menuObjects={helpMenus}
                      >
                        <li
                          style={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                          }}
                        >
                          {t('help')}
                          <ChevronIcon className={classes.chevronIcon} />
                        </li>
                      </DropdownMenuSp>
                    </ul>
                  </nav>
                </div>
                <div className={clsx(classes.headerRight, classes.navDiv)}>
                  {userData ? (
                    renderMemberInfoDesktop()
                  ) : (
                    <div className={classes.memberArea}>
                      <Link href={toOld('/login')} passHref>
                        <a title={t('login')}>
                          <p className={classes.login}>{t('login')}</p>
                        </a>
                      </Link>
                      <Link href={toOld('/register')} passHref>
                        <a title={t('get-started')}>
                          <ButtonSp className={classes.regisBtn}>
                            {t('get-started')}
                          </ButtonSp>
                        </a>
                      </Link>
                    </div>
                  )}
                  <div
                    style={{
                      width: 1,
                      height: 27,
                      backgroundColor: '#BDBDBD',
                      margin: `0px ${theme.spacing(2)}px`,
                    }}
                  />
                  {renderLangSwitch()}
                </div>
              </header>
            </Container>
          </div>
          <div className={classes.headerSpacerDesktop} />
        </>
      </Hidden>
      <Hidden mdUp implementation="css">
        <AppBar color="secondary" elevation={0} className={classes.appBar}>
          <Toolbar>
            <Link href={toOld('/')} passHref>
              <a
                title="SHIPPOP ส่งของ ส่งพัสดุด่วน เปรียบเทียบราคาขนส่ง"
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                }}
              >
                <img
                    src={asset.logo}
                    className={logoStyle.header.classNameResponsive}
                    alt="SHIPPOP ส่งของ ส่งพัสดุด่วน เปรียบเทียบราคาขนส่ง"
                />
              </a>
            </Link>
            <div style={{ flexGrow: 1 }} />
            <IconButton
              onClick={() => setDrawerOpen(!isDrawerOpen)}
              style={{ marginRight: -12 }}
            >
              {isDrawerOpen ? (
                <CloseIcon fontSize="large" />
              ) : (
                <MenuIcon fontSize="large" />
              )}
            </IconButton>
          </Toolbar>
        </AppBar>
        <Drawer
          anchor="top"
          open={isDrawerOpen}
          onClose={() => setDrawerOpen(false)}
          style={{ zIndex: theme.zIndex.appBar - 1 }}
        >
          <div
            style={{
              width: '100vw',
              minHeight: '100vh',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <div
              style={{
                height: theme.homeStyle.headerHeightMobile,
                minHeight: theme.homeStyle.headerHeightMobile,
              }}
            />
            <List>
              {renderListItemLink(t('home-page'), toOld('/'))}
              {renderListItemCollapse(
                t('our-services'),
                isOurServiceOpen,
                toggleOurService,
                <List component="div">
                  {ourServicesMenus.map((item) =>
                    renderListItemLink(item.label, item.href, classes.pl4)
                  )}
                </List>
              )}
              {[
                ['SHIPPOP PRO', '/pro'],
                [t('about-us'), '/about'],
                [t('tracking'), '/tracking'],
              ].map((item, index) => (
                <Link href={toOld(item[1])} passHref key={index}>
                  <a title={item[0]}>
                    <ListItem
                      button
                      classes={{ root: classes.listItemPaddingY }}
                    >
                      <ListItemText
                        primary={item[0]}
                        className={clsx(
                          classes.pl1,
                          isCurrentLink(item[1]) && classes.activeLink
                        )}
                      />
                    </ListItem>
                  </a>
                </Link>
              ))}
              {renderListItemCollapse(
                t('help'),
                isHelpOpen,
                toggleHelp,
                <List component="div">
                  {helpMenus.map((item) => {
                    if (!item.nested)
                      return renderListItemLink(
                        item.label,
                        item.href,
                        classes.pl4
                      )
                    else
                      return renderListItemCollapse(
                        item.label,
                        item.label === t('faq') ? isFaqOpen : isToolsOpen,
                        item.label === t('faq') ? toggleFaq : toggleTools,
                        <List component="div">
                          {item.nested.map((item) =>
                            renderListItemLink(
                              item.label,
                              item.href,
                              classes.pl7
                            )
                          )}
                        </List>,
                        classes.pl4
                      )
                  })}
                </List>
              )}
            </List>
            <Divider
              style={{
                margin: `${theme.spacing(1)}px 0`,
                backgroundColor: '#ebedf0',
              }}
            />
            <div
              style={{ height: theme.spacing(1), minHeight: theme.spacing(1) }}
            />
            {!userData ? (
              <Container>
                <Grid container direction="column" alignItems="center">
                  <Link href={toOld('/register')} passHref>
                    <a
                      title={t('get-started')}
                      style={{
                        width: '100%',
                        margin: '0 0px 16px 0px',
                        height: 50,
                      }}
                    >
                      <ButtonSp
                        fullWidth
                        style={{
                          height: 50,
                        }}
                      >
                        {t('get-started')}
                      </ButtonSp>
                    </a>
                  </Link>
                  <Link href={toOld('/login')} passHref>
                    <a title={t('login')}>
                      <ButtonSp variant="text" style={{ color: '#404041' }}>
                        {t('login')}
                      </ButtonSp>
                    </a>
                  </Link>
                </Grid>
              </Container>
            ) : (
              renderMemberInfoMobile()
            )}
            <Divider
              style={{
                marginTop: theme.spacing(2),
                marginBottom: theme.spacing(5),
                backgroundColor: '#ebedf0',
              }}
            />
            {renderLangSwitch()}
          </div>
        </Drawer>
        <div className={classes.headerSpacerMobile} />
      </Hidden>
    </>
  )
}

export default Header
